import * as _ from 'lodash'

import i18n from '@/i18n'
import customerApi from '@/libs/api/customerApi'
import dimorderApi, { DimorderApi } from '@/libs/api/dimorderApi'
import checkCRMPermission, { parseJwt } from '@/libs/checkCRMPermission'

import actions from '../actions'
import ActionTypes from './ActionTypes'

async function mapMerchants(items, load) {
  const results = new Array(items.length)
  let nextIndex = 0
  await Promise.all(
    Array.from({ length: Math.min(5, items.length) }, async () => {
      while (nextIndex < items.length) {
        const index = nextIndex++
        results[index] = await load(items[index])
      }
    }),
  )
  return results
}

/**
 * @function
 * @returns {ThunkFunction}
 */
export function init(customerInfo) {
  return async (dispatch, getState) => {
    if (customerInfo?.data?.platforms) {
      await dispatch(fetchMerchants(customerInfo))

      const savedId = localStorage.getItem('activeMerchant')
      const merchants = getState().merchant.merchants
      const candidates = [
        ...merchants.filter((merchant) => merchant.id === savedId),
        ...merchants.filter((merchant) => merchant.id !== savedId),
      ]
      for (const merchant of candidates) {
        try {
          await dispatch(activateMerchantById(merchant.id))
          break
        } catch (error) {
          console.error('activateMerchant error', merchant.id, error)
        }
      }
    }

    dispatch({
      type: ActionTypes.INIT,
      payload: {},
    })
  }
}

export function fetchCRMGroups() {
  return async (dispatch, getState) => {
    const groups = {}
    const merchants = getState().merchant.merchants
    const accountsVersion = getState().merchant.accountsVersion
    const isAdmin = dispatch(actions.user.isAdmin())

    const userCountRequests = new Map()
    const merchantGroups = await mapMerchants(merchants, async (merchant) => {
      try {
        const hasCRMPermission =
          isAdmin || (await checkCRMPermission(merchant.id, merchant.username))
        if (!hasCRMPermission) return

        const api = dimorderApi.instance(merchant.id)
        const group = await api.getCRMGroup()
        if (!group) return

        const printerSetting = await api.getPrinterSetting()
        // 同一個 group 共用會員數請求，避免並行商戶重複查詢。
        if (!userCountRequests.has(group.id)) {
          userCountRequests.set(
            group.id,
            api.getUserCount().catch((error) => {
              console.error('fetchCRMGroups user count error', group.id, error)
              return { total: 0 }
            }),
          )
        }
        const userCount = await userCountRequests.get(group.id)
        return { group, merchant: { ...merchant, printerSetting }, userCount }
      } catch (error) {
        console.error('fetchCRMGroups error', merchant.id, error)
      }
    })

    // 新增帳號、重新登入或登出後，忽略舊請求的結果及 API 綁定。
    if (getState().merchant.accountsVersion !== accountsVersion) return

    for (const result of merchantGroups) {
      if (!result) continue
      const { group, merchant, userCount } = result
      dimorderApi.addGroup(group.id, merchant.id)
      if (!groups[group.id]) {
        groups[group.id] = {
          ...group,
          totalUserCount: userCount?.total ?? 0,
          groupMerchants: {},
        }
      }
      groups[group.id].groupMerchants[merchant.id] = merchant
    }

    dispatch({
      type: ActionTypes.UPDATE_CRM_GROUPS,
      payload: { groups },
    })
  }
}

export function updateCRMGroup(data) {
  return async (dispatch, getState) => {
    const crmGroups = getState().merchant.crmGroups
    const group = crmGroups[data?.id]
    if (_.isEmpty(group)) return
    try {
      dispatch({
        type: ActionTypes.UPDATE_CRM_GROUP,
        payload: { group: { ...group, ...data } },
      })
    } catch (error) {
      console.error('updateCRMGroup error', error)
    }
  }
}

export function fetchMerchants(customerInfo) {
  return async (dispatch) => {
    const merchants = await mapMerchants(
      customerInfo.data.platforms.filter((platform) => platform.name === 'dimorder-merchant'),
      async (platform) => {
        try {
          return await dispatch(addMerchant(platform, 'name'))
        } catch (error) {
          console.error('fetchMerchants error', platform.uid, error)
        }
      },
    )

    dispatch({ type: ActionTypes.ADD_MERCHANT, payload: merchants.filter(Boolean) })
  }
}

export function activateMerchantById(merchantId) {
  return async (dispatch, getState) => {
    const merchants = getState().merchant.merchants
    let activeMerchant = merchants.find((merchant) => merchant.id === merchantId)
    if (activeMerchant) {
      const api = dimorderApi.instance(merchantId)
      if (!activeMerchant.setting) {
        activeMerchant = toMerchantItem(await api.getMerchant(), activeMerchant.username)
        dispatch(updateMerchantById(merchantId, activeMerchant))
      }
      const categories = await api.getCategories()

      dispatch({
        type: ActionTypes.ACTIVE_MERCHANT,
        payload: {
          ...activeMerchant,
          categories: Object.entries(categories?.data).map((category) => {
            return {
              id: category[0],
              name: category[1],
            }
          }),
        },
      })
      localStorage.setItem('activeMerchant', merchantId)
    }
  }
}

export function loginMerchant(params) {
  return async (dispatch, getState) => {
    try {
      const response = await DimorderApi.scopelogin(params)
      const permissions = _.get(response, 'permissions', [])

      if (permissions.includes('admin') || permissions.includes('merchant-portal')) {
        await customerApi.updateMerchant(response.token, response.refresh)
        const accounts = await dispatch(actions.user.getCustomerInfo())
        const merchants = getState().merchant.merchants
        const platform = accounts.data.platforms.find(
          (platform) =>
            platform.name === 'dimorder-merchant' &&
            ((response.id && platform.uid === response.id) ||
              platform.access_token === response.token),
        )
        const added = platform ? [await dispatch(addMerchant(platform))] : []
        const merged = new Map(merchants.map((merchant) => [merchant.id, merchant]))
        for (const merchant of added) merged.set(merchant.id, merchant)
        dispatch({ type: ActionTypes.ADD_MERCHANT, payload: [...merged.values()] })
        const activeId = getState().merchant.activeMerchant?.id
        if (!activeId && added.length) {
          await dispatch(activateMerchantById(added[0].id))
        } else if (added.some((merchant) => merchant.id === activeId)) {
          await dispatch(activateMerchantById(activeId))
        }
        dispatch({ type: ActionTypes.INIT, payload: {} })
      } else {
        throw new Error('!permission')
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }
}

export function addMerchant(platform, view) {
  return async (dispatch, getState) => {
    const { identifier } = parseJwt(platform.access_token)

    /**
     *
     * @param {Error} error
     * @param {string} merchantName
     * @param {string} merchantShortCode
     * @param {string} username
     */
    const refreshFailedCallback = (error, merchantName, merchantShortCode, username) => {
      dispatch(
        actions.merchant.openMerchantLoginDialog({
          data: {
            title: i18n.t('app.component.addMerchantModal.expiredMerchant', { merchantName }),
            merchantShortCode,
            username,
          },
        }),
      )
      console.log('refresh failed', error)
    }

    const apiInstance = dimorderApi.create(
      platform.uid,
      identifier,
      platform.access_token,
      platform.refresh_token,
      refreshFailedCallback,
    )
    const merchantData = await apiInstance.getMerchant(view)
    if (view === 'name') {
      return { id: merchantData.id, name: merchantData.name, username: identifier }
    }
    return toMerchantItem(merchantData, identifier)
  }
}

function toMerchantItem(merchantData, username) {
  return {
    name: merchantData.name,
    id: merchantData.id,
    username,
    address: merchantData.address,
    image: merchantData.images?.cover,
    payments: merchantData.payments ?? [],
    setting: merchantData.setting,
    rounding: merchantData.rounding,
    region: merchantData.region,
    keetaShopId: merchantData.keetaShopId,
  }
}

export function updateMerchantById(merchantId, data) {
  return async (dispatch) => {
    dispatch({
      type: ActionTypes.UPDATE_MERCHANT,
      payload: { merchantId, data },
    })
  }
}

/**
 * open the merchant login dialog
 * @typedef OpenMerchantLoginDialogParams
 * @property {object} data
 * @property {string} [data.title='']
 * @property {string} [data.merchantName='']
 * @property {string} [data.username='']
 * @property {string} [data.merchantShortCode='']
 * @property {boolean} [firstLogin=false]
 * @property {() => void} [onSuccess]
 * @param {OpenMerchantLoginDialogParams} params
 * @returns {ThunkFunction}
 */
export function openMerchantLoginDialog({ data, firstLogin = false, onSuccess = () => {} }) {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.OPEN_MERCHANT_LOGIN_DIALOG,
      payload: {
        data,
        firstLogin,
        onSuccess,
      },
    })
  }
}

/**
 * control the merchant login dialog close and reset dialog data
 * @returns {ThunkFunction}
 */
export function closeMerchantLoginDialog() {
  return (dispatch) => {
    dispatch({
      type: ActionTypes.CLOSE_MERCHANT_LOGIN_DIALOG,
    })
  }
}
